import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Buildings } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MotelsPublic() {
  const [board, setBoard] = useState(null);
  const [market, setMarket] = useState("");

  useEffect(() => {
    api.get("/motels/public", { params: market ? { market } : {} })
      .then(({ data }) => setBoard(data))
      .catch(() => setBoard({ motels: [], markets: [] }));
  }, [market]);

  useSeo({
    title: "Motels near the yard and on the route | Unity & Utility",
    description: (board?.blurb || "Beds near our yards for shift workers and along the freight routes for drivers.").slice(0, 180),
    path: "/motels",
    image: board?.picture ? `${BACKEND}${board.picture}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Motels", "/motels"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="motels-public-page">
        <Crumbs items={[["Home", "/"], ["Motels", "/motels"]]} />
        <p className="overline text-[#B3241B]">A bed near the yard or on the route</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Buildings size={34} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
          Motels
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="motels-blurb">
          {board?.blurb || "Beds near our yards for shift workers between long shifts, and along the freight routes for drivers on the road."}
        </p>
        <div className="mt-5">
          <ShareBar url={`${BACKEND}/api/share/motels`} text="Motels near the yard and on the route"
            testid="motels-public-share" instagram />
        </div>

        {board?.markets?.length > 1 && (
          <div className="scroll-x mt-8 flex gap-1.5 overflow-x-auto" data-testid="motels-markets">
            {[["", "Everywhere"], ...board.markets.map((m) => [m, m])].map(([key, label]) => (
              <button key={key || "all"} data-testid={`motels-market-${key || "all"}`} onClick={() => setMarket(key)}
                className={`tap-safe shrink-0 border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                  market === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {board?.motels?.length === 0 ? (
          <p className="mt-8 text-sm text-slate-500" data-testid="motels-public-empty">
            No motels on the board for that filter just now.
          </p>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="motels-public-list">
            {board?.motels?.map((m, i) => (
              <div key={m.id} data-testid={`motels-public-${i}`} className="border border-border bg-white">
                {m.image_url && (
                  <Link to={`/motels/${m.id}`}>
                    <img src={`${BACKEND}${m.image_url}`} alt={m.name} className="h-44 w-full object-cover" />
                  </Link>
                )}
                <div className="p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">{m.town}</p>
                  <Link to={`/motels/${m.id}`} className="mt-1 block font-semibold text-[#0F172A] hover:text-[#002FA7]">
                    {m.name}
                  </Link>
                  <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{m.symbol}{m.nightly_rate}
                    <span className="text-xs font-semibold text-slate-500"> a night</span></p>
                  <div className="mt-2">
                    <ShareBar url={`${BACKEND}/api/share/motel/${m.id}`} text={m.name}
                      testid={`motels-public-share-${i}`} compact instagram />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicShell>
  );
}

export function MotelPublic() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/motels/public/${id}`).then(({ data }) => setM(data))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Not on the board"));
  }, [id]);

  useSeo({
    title: m ? `${m.name}, ${m.town} | Motels | Unity & Utility` : "Motels | Unity & Utility",
    description: (m?.description || m?.name || "From our motel board.").slice(0, 180),
    path: `/motels/${id}`,
    image: m?.image_url ? `${BACKEND}${m.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Motels", "/motels"], [m?.name || "Motel", null]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="motel-page">
        <Crumbs items={[["Home", "/"], ["Motels", "/motels"], [m?.name || "Motel", null]]} />
        {error && <p data-testid="motel-error" className="mt-6 text-sm text-slate-500">{error}</p>}
        {m && (
          <>
            <p className="overline text-[#B3241B]">{m.town}{m.near_site ? ` · near ${m.near_site}` : ""}</p>
            <h1 data-testid="motel-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
              {m.name}
            </h1>
            {m.image_url && (
              <img data-testid="motel-image" src={`${BACKEND}${m.image_url}`} alt={m.name}
                className="mt-6 w-full border border-border object-cover" />
            )}
            <p data-testid="motel-rate" className="mt-5 text-2xl font-bold text-[#0B5C2E]">
              {m.symbol}{m.nightly_rate}
              <span className="ml-2 align-middle text-xs font-semibold uppercase tracking-wide text-slate-500">a night · {m.rooms} rooms</span>
            </p>
            {m.description && (
              <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-700">{m.description}</p>
            )}
            {m.amenities?.length > 0 && (
              <ul className="mt-4 grid gap-1 text-sm text-slate-700 sm:grid-cols-2" data-testid="motel-amenities">
                {m.amenities.map((a, i) => <li key={i}>· {a}</li>)}
              </ul>
            )}
            <div className="mt-6">
              <ShareBar url={`${BACKEND}/api/share/motel/${id}`} text={m.name}
                testid="motel-share" instagram />
            </div>
            <Link to="/motels" data-testid="motel-back"
              className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
              See the whole motel board
            </Link>
          </>
        )}
      </div>
    </PublicShell>
  );
}
